import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSelectedRestaurant } from '../../context/SelectedRestaurantContext';
import { promotionsService, type Promotion } from '../../services/promotions';
import InlineAlert from '../../components/InlineAlert';

export default function PromotionsPage() {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [items, setItems] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<Promotion> & { id?: string } | null>(null);

  const canSubmit = useMemo(() => !!editing?.title && !!editing, [editing?.title, editing]);

  const load = async () => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true); setError(null);
      const resp: any = await promotionsService.list(user.uid, selectedRestaurantId);
      const data = Array.isArray(resp?.data?.data) ? resp.data.data : (resp?.data || []);
      setItems(data);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar las promociones');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [user?.uid, selectedRestaurantId]);

  const startCreate = () => setEditing({ title: '', description: '', enabled: true, type: 'generic', images: [], config: { priority: 1 } });
  const startEdit = (p: Promotion) => setEditing({ ...p });
  const cancelEdit = () => setEditing(null);

  const save = async () => {
    if (!user?.uid || !selectedRestaurantId || !editing) return;
    try {
      setLoading(true); setError(null);
      if (editing.id) {
        // En edición no se permite modificar imágenes
        const { images: _imgs, ...rest } = editing as any;
        await promotionsService.update(user.uid, selectedRestaurantId, editing.id, rest);
      } else {
        // Crear y subir archivos a los signed URLs devueltos
        const fileInput = document.querySelector('#promo-file-input') as HTMLInputElement | null;
        const files = fileInput?.files ? Array.from(fileInput.files) : [];
        const imagesFilenames = files.length ? files.map(f => ({ filename: f.name })) : undefined;

        const created: any = await promotionsService.create(
          user.uid,
          selectedRestaurantId,
          { ...(editing as any), imagesFilenames }
        );
        const promotion = created?.data?.promotion || created?.promotion || created?.data?.promotion || created?.data;
        const uploadUrls: Array<{ signedUrl: string; publicUrl: string }> = created?.data?.uploadUrls || created?.uploadUrls || [];

        if (uploadUrls.length && files.length) {
          const count = Math.min(uploadUrls.length, files.length);
          await Promise.all(
            uploadUrls.slice(0, count).map((u, idx) =>
              fetch(u.signedUrl, {
                method: 'PUT',
                headers: { 'Content-Type': files[idx]?.type || 'image/jpeg' },
                body: files[idx]
              })
            )
          );
        }
        if (promotion?.id) editing.id = promotion.id;
      }
      setEditing(null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo guardar la promoción');
    } finally { setLoading(false); }
  };

  const remove = async (p: Promotion) => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setLoading(true); setError(null);
      await promotionsService.remove(user.uid, selectedRestaurantId, p.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo eliminar');
    } finally { setLoading(false); }
  };

  const toggleEnabled = async (p: Promotion) => {
    if (!user?.uid || !selectedRestaurantId) return;
    try {
      setItems(prev => prev.map(x => x.id === p.id ? { ...x, enabled: !x.enabled } as Promotion : x));
      await promotionsService.update(user.uid, selectedRestaurantId, p.id, { enabled: !p.enabled });
    } catch (e: any) {
      setError(e?.message || 'No se pudo cambiar el estado');
      // revertir
      setItems(prev => prev.map(x => x.id === p.id ? { ...x, enabled: p.enabled } as Promotion : x));
    }
  };

  // Subida de archivos: solo al crear (input en el modal)

  return (
    <div className="min-h-screen bg-[#e9ecef] px-4 py-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
          <h1 className="text-2xl font-bold text-[#2C4358]">Promociones</h1>
          <button onClick={startCreate} className="px-4 py-2 rounded-xl bg-[#22B6B2] text-white font-semibold hover:opacity-90 w-full sm:w-auto">Nueva</button>
        </div>

        {error && <div className="mb-4"><InlineAlert type="error" message={error} onClose={() => setError(null)} /></div>}

        {loading ? (
          <div className="text-center text-[#2C4358]">Cargando...</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(p => (
              <div key={p.id} className="bg-white rounded-2xl shadow-md border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-bold text-[#2C4358]">{p.title}</div>
                    <div className="text-xs text-[#85929e]">{p.type}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {/* Badge prioridad (solo si está activa) */}
                      {p.enabled && (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${((p as any)?.config?.priority ?? 1) === 2 ? 'bg-red-50 text-red-700 border-red-200' : ((p as any)?.config?.priority ?? 1) === 1 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-700 border-gray-200'}`}
                          title="Prioridad de rotación"
                        >
                          {((p as any)?.config?.priority ?? 1) === 2 ? 'Alta prioridad' : ((p as any)?.config?.priority ?? 1) === 1 ? 'Prioridad media' : 'Prioridad baja'}
                        </span>
                      )}
                      {/* Badge estado */}
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${p.enabled ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                      >
                        {p.enabled ? 'Activa' : 'Inactiva'}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <button
                      onClick={() => toggleEnabled(p)}
                      className={`px-2 py-1 text-xs rounded-lg border ${p.enabled ? 'border-green-200 text-green-700 hover:bg-green-50' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                      title={p.enabled ? 'Desactivar' : 'Activar'}
                    >
                      {p.enabled ? 'Desactivar' : 'Activar'}
                    </button>
                    <button onClick={() => startEdit(p)} className="px-2 py-1 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Editar</button>
                    <button onClick={() => remove(p)} className="px-2 py-1 text-xs rounded-lg border border-red-200 text-red-700 hover:bg-red-50">Eliminar</button>
                  </div>
                </div>
                {p.images && p.images[0]?.url && (
                  <img src={p.images[0].url} alt={p.images[0].alt || ''} className="w-full h-32 sm:h-28 object-cover rounded-xl mt-3" />
                )}
                {p.description && <div className="text-sm text-[#2C4358] mt-2 line-clamp-2">{p.description}</div>}
              </div>
            ))}
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-6 mx-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-[#2C4358]">{editing.id ? 'Editar' : 'Nueva'} Promoción</h3>
                <button onClick={cancelEdit} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
              </div>
              <div className="space-y-3">
                <input value={editing.title || ''} onChange={e => setEditing({ ...editing!, title: e.target.value })} placeholder="Título" className="w-full px-3 py-2 rounded-lg border border-gray-300" />
                <textarea value={editing.description || ''} onChange={e => setEditing({ ...editing!, description: e.target.value })} placeholder="Descripción" className="w-full px-3 py-2 rounded-lg border border-gray-300 min-h-[80px]" />
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#85929e]">Tipo</label>
                    <select value={(editing.type as any) || 'generic'} onChange={e => setEditing({ ...editing!, type: e.target.value as any })} className="w-full px-3 py-2 rounded-lg border border-gray-300">
                      <option value="generic">Genérica</option>
                      <option value="discount">Descuento</option>
                      <option value="bundle">Paquete</option>
                      <option value="happy_hour">Happy Hour</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    <input type="checkbox" checked={!!editing.enabled} onChange={e => setEditing({ ...editing!, enabled: e.target.checked })} />
                    <span className="text-sm">Activa</span>
                  </div>
                </div>
                {/* Link/CTA y snooze opcional */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#85929e]">URL de acción (opcional)</label>
                    <input
                      value={(editing.config as any)?.link || ''}
                      onChange={e => setEditing({ ...editing!, config: { ...(editing.config || {}), link: e.target.value } })}
                      placeholder="https://tusitio.com/promo"
                      className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-[#85929e]">Texto del botón</label>
                    <input
                      value={(editing.config as any)?.ctaText || ''}
                      onChange={e => setEditing({ ...editing!, config: { ...(editing.config || {}), ctaText: e.target.value } })}
                      placeholder="Ver promoción"
                      className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#85929e]">Prioridad</label>
                    <select
                      value={(editing.config as any)?.priority ?? 1}
                      onChange={e => setEditing({ ...editing!, config: { ...(editing.config || {}), priority: parseInt(e.target.value, 10) } })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    >
                      <option value={2}>Alta</option>
                      <option value={1}>Media</option>
                      <option value={0}>Baja</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-[#85929e]">No mostrar por (días)</label>
                    <input
                      type="number"
                      min={1}
                      value={(editing.config as any)?.snoozeDays ?? 7}
                      onChange={e => setEditing({ ...editing!, config: { ...(editing.config || {}), snoozeDays: Math.max(1, parseInt(e.target.value || '7', 10)) } })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="datetime-local" value={(editing.start_at as any) || ''} onChange={e => setEditing({ ...editing!, start_at: e.target.value })} className="px-3 py-2 rounded-lg border border-gray-300" />
                  <input type="datetime-local" value={(editing.end_at as any) || ''} onChange={e => setEditing({ ...editing!, end_at: e.target.value })} className="px-3 py-2 rounded-lg border border-gray-300" />
                </div>
                {/* Ventana horaria removida: se usa rango en calendario */}
                {/* Imágenes */}
                <div>
                  <label className="text-xs text-[#85929e]">Imágenes</label>
                  {!editing.id ? (
                    <div className="mb-2">
                      <div className="text-[11px] text-[#85929e] mb-1">Selecciona imágenes (se subirán al guardar)</div>
                      <input id="promo-file-input" type="file" accept="image/*" multiple />
                    </div>
                  ) : (
                    <div className="text-[11px] text-[#85929e] mb-2">Las imágenes se definen al crear la promoción.</div>
                  )}
                  {editing.id && (editing.images || []).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {(editing.images || []).map((im, idx) => (
                        <div key={idx} className="relative">
                          <img src={im.url} alt={im.alt || ''} className="w-full h-20 object-cover rounded-lg" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button onClick={cancelEdit} className="px-4 py-2 rounded-xl border border-gray-200">Cancelar</button>
                <button disabled={!canSubmit || loading} onClick={save} className="px-4 py-2 rounded-xl bg-[#22B6B2] text-white font-semibold disabled:opacity-50">Guardar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


